import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, IconButton, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { useAuth_AuthenticateMutation } from 'queries';
import { Eye } from 'ts-react-feather-icons';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import MFAPrompt from './mfa-prompt';

const useStyles = makeStyles(() => ({
  root: {
    marginTop: '5vh',
    paddingLeft: '15vw',
    paddingRight: '15vw',
  },
  input: {
    display: 'flex',
    margin: 'auto',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  dialog: {
    textAlign: 'center',
  },
  iconButton: {
    padding: '6px',
  },
}));

const schema = yup.object().shape({
  email: yup.string().required().email('Please enter a valid email'),
  password: yup.string().required(),
});

type LoginCredentials = {
  email: string;
  password: string;
};

const LoginForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const [requireMFA, setRequireMFA] = useState(false);
  const [credentials, setCredentials] = useState<LoginCredentials | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<LoginCredentials>({ resolver: yupResolver(schema) });

  const [authenticate, { loading: authenticateExecuting }] = useAuth_AuthenticateMutation({
    onCompleted: () => setRequireMFA(true),
    onError: (e) =>
      showAlert({
        message: `Error with login: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const onSubmit = async (formData: LoginCredentials) => {
    setCredentials(formData);
    await authenticate({ variables: { email: formData.email } });
  };

  return (
    <>
      <MFAPrompt open={requireMFA} onClose={() => setRequireMFA(false)} credentials={credentials} />
      <form className={classes.root} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <Controller
            name="email"
            control={control}
            render={({ field: { ref, name, onBlur, onChange, value } }) => (
              <FormTextField
                name={name}
                defaultValue={value}
                onChange={onChange}
                onBlur={onBlur}
                ref={ref}
                errorMessage={errors.email?.message}
                label="Email"
                className={clsx(classes.input, errors.email && classes.inputError)}
                data-cy="sign-in-email"
              />
            )}
          />
        </div>
        <div style={{ marginTop: '2vh', marginBottom: '15px' }}>
          <Controller
            name="password"
            control={control}
            render={({ field: { ref, name, onBlur, onChange, value } }) => (
              <FormTextField
                name={name}
                defaultValue={value}
                onChange={onChange}
                onBlur={onBlur}
                ref={ref}
                errorMessage={errors.password?.message}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                className={clsx(classes.input, errors.password && classes.inputError)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => setShowPassword(!showPassword)}
                      size="large"
                    >
                      <Eye />
                    </IconButton>
                  ),
                }}
                data-cy="sign-in-password"
              />
            )}
          />
        </div>
        <Typography style={{ display: 'inline', textDecoration: 'underline', cursor: 'pointer' }}>
          <Link onClick={() => history.push('/reset-password')}>Reset your password</Link>
        </Typography>
        <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '60px' }}>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={isSubmitting || authenticateExecuting}
          >
            Sign in
          </Button>
        </div>
      </form>
    </>
  );
};

export default LoginForm;
